import type { ReactNode } from 'react';
import { BrowserLocaleSource, LocaleSwitcher, StoredLocaleSource, resolveSupportedLocale } from '@talbenmoshe/prometheus-i18n-runtime';
import type { ILocaleController, ILocaleSwitcher, LocaleBundles } from '@talbenmoshe/prometheus-i18n-runtime';
import { ReactTranslator } from '@talbenmoshe/prometheus-i18n-react';
import { Messages, loadMessages } from '@talbenmoshe/naru-i18n';
import type { IMessages } from '@talbenmoshe/naru-i18n';

const FALLBACK_LOCALE = 'en';
// English is the only language this app offers for now; the Hebrew messages stay in naru-i18n for later.
const APP_LOCALES = [FALLBACK_LOCALE];
const LOCALE_STORAGE_KEY = 'naru-site.locale';

export interface AppTranslation {
  readonly messages: IMessages<ReactNode>;
  readonly localeController: ILocaleController;
  readonly localeSwitcher: ILocaleSwitcher;
}

async function loadBundles(locale: string): Promise<LocaleBundles> {
  return { messages: await loadMessages(locale) };
}

/**
 * Composes translation for this app. It offers English only, so a remembered or browser locale this app does not
 * offer (Hebrew included) resolves to English.
 */
export async function createAppTranslator(): Promise<AppTranslation> {
  const localeSource = new StoredLocaleSource(
    window.localStorage,
    new BrowserLocaleSource(window.navigator, FALLBACK_LOCALE),
    LOCALE_STORAGE_KEY
  );

  const locale = resolveSupportedLocale(await localeSource.getLocale(), APP_LOCALES) ?? FALLBACK_LOCALE;
  const translator = await ReactTranslator.create({ locale, fallbackLocale: FALLBACK_LOCALE, bundles: await loadBundles(locale) });

  return {
    messages: new Messages<ReactNode>(translator),
    localeController: translator,
    localeSwitcher: new LocaleSwitcher(translator, loadBundles, localeSource, APP_LOCALES)
  };
}
